"use client";

import React, { useEffect, useState } from "react";
import { Search, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
function SearchPopover() {
  const [searchValue, setSearchValue] = useState("");
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground hover:text-primary transition-colors">
          <Search className="size-5" />
          <span className="sr-only">Search arrangements</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 border-border bg-popover/90 backdrop-blur-lg">
        <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search flowers & gifts..." className="w-full" />
      </PopoverContent>
    </Popover>;
}
function HeaderMenuConsumer({
  isMobileOpen,
  setIsMobileOpen
}: {
  isMobileOpen: boolean;
  setIsMobileOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-2">
        <div className="h-9 w-24 bg-muted/30 animate-pulse rounded-lg" />
        <div className="h-9 w-24 bg-muted/30 animate-pulse rounded-lg" />
      </div>;
  }
  return <>
      {}
      <div className="hidden md:block">
        <NavigationMenu viewport={true}>
          <NavigationMenuList className="justify-center">
            {menuItems.map((item, i) => {
            const itemHref = item.object_type === "category" ? `/term/product_cat/${item.object_id}` : item.object_type === "post_type" ? item.href || "#" : item.href || "#";
            return <NavigationMenuItem key={item.id} data-index={i}>
                {item.children && item.children.length > 0 ? <>
                    <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-accent/30 hover:text-primary data-[state=open]:bg-accent/30 data-[state=open]:text-primary font-default font-medium tracking-wider">
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-popover/95 backdrop-blur-2xl border-border rounded-2xl">
                        {item.children.map((child, j) => {
                      const childHref = child.object_type === "category" ? `/term/product_cat/${child.object_id}` : child.object_type === "post_type" ? child.href || "#" : child.href || "#";
                      return <li key={child.id} data-index={j}>
                            <NavigationMenuLink asChild>
                              <Link to={childHref} className="block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/40 hover:text-primary focus:bg-accent/40 focus:text-primary">
                                <div className="text-sm font-default font-medium leading-none text-foreground">{child.label}</div>
                              </Link>
                            </NavigationMenuLink>
                          </li>;
                    })}
                      </ul>
                    </NavigationMenuContent>
                  </> : <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), "bg-transparent text-foreground hover:bg-accent/30 hover:text-primary font-default font-medium tracking-wider")}>
                    <Link to={itemHref}>{item.label}</Link>
                  </NavigationMenuLink>}
              </NavigationMenuItem>;
          })}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {}
      {isMobileOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-2xl border-b border-border shadow-xl md:hidden flex flex-col p-4 gap-2 z-50">
          {menuItems.map((item, i) => {
        const itemHref = item.object_type === "category" ? `/term/product_cat/${item.object_id}` : item.object_type === "post_type" ? item.href || "#" : item.href || "#";
        return <div key={item.id} data-index={i} className="flex flex-col gap-2">
              <Link to={itemHref} className="text-lg font-default font-medium p-2 text-foreground hover:text-primary transition-colors" onClick={() => setIsMobileOpen(false)}>
                {item.label}
              </Link>
              {item.children && item.children.length > 0 && <div className="pl-4 flex flex-col gap-2 border-l border-border/30 ml-2">
                  {item.children.map((child, j) => {
              const childHref = child.object_type === "category" ? `/term/product_cat/${child.object_id}` : child.object_type === "post_type" ? child.href || "#" : child.href || "#";
              return <Link key={child.id} data-index={j} to={childHref} className="text-sm font-default text-muted-foreground hover:text-primary transition-colors p-2" onClick={() => setIsMobileOpen(false)}>
                      {child.label}
                    </Link>;
            })}
                </div>}
            </div>;
      })}
        </div>}
    </>;
}
export default function GlobalNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="224565" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500", "bg-background/60 md:bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-secondary shadow-sm" : "border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" className="flex items-center">
            <img src="https://flowing-boar.10web.cloud/wp-content/uploads/2026/05/cropped-Adobe-Express-file.png" alt="Eternal Blooms" className="object-contain !h-auto !w-[90px] mt-[1cm]" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex justify-center">
          <MenuProvider menu_id="32">
            <HeaderMenuConsumer isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          <SearchPopover />
          <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary transition-colors" iconSize="size-5" />
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary transition-colors ml-1" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label="Toggle Menu">
            {isMobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>
      </div>
    </section>;
}